import { format as formatDate, isDate, isValid } from 'date-fns';
import i18n from 'i18next';
import TranslationsLoader from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { brandConfig } from '@common/brand/brandConfig';
import { fallbackLng } from '@common/i18n/constants';
import { supportedLanguages } from '@common/i18n/supportedLocales';
import analytics from '@common/utils/analytics';
import parseQueryString from '@common/utils/formatting/parseQueryString';
import localStorageManager from '@common/utils/storage/localStorageManager';
import { LOCAL_STORAGE_LANGUAGE_STORAGE_KEY } from '@common/utils/storage/storageKeys';

const localNamespace = 'local';
const remoteNamespace = 'remote';

let translationLoggerEnabled = false;

if (APP_TYPE === 'sw' || APP_TYPE === 'um') {
  i18n.use({
    type: 'postProcessor',
    name: 'translationLogger',
    process: (value: string, key: string) => {
      if (translationLoggerEnabled) {
        console.debug(`i18n: [${key}] => [${value}]`);
      }
      return value;
    },
  });
  analytics.addActionForEnableTranslationLogger((enabled) => {
    translationLoggerEnabled = enabled;
  });
}

const queryParams = parseQueryString(window.location.search);

// Replaces i18next's built-in formatter: that one lowercases the format (dd/MM/yyyy -> dd/mm/yyyy,
// where mm means minutes) and applies its own Intl formats; this one only formats dates.
i18n.use({
  type: 'formatter',
  format: (value: unknown, format?: string) => {
    if (!isDate(value)) {
      return value;
    }
    return isValid(value) ? formatDate(value, format ?? '') : '';
  },
});

i18n
  .use(TranslationsLoader)
  .use(initReactI18next)
  .init({
    fallbackLng,
    lng: Object.keys(queryParams).includes('naked')
      ? 'cimode'
      : localStorageManager.getItem<string>(LOCAL_STORAGE_LANGUAGE_STORAGE_KEY)
        ?? process.env.INITIAL_LANGUAGE,
    postProcess: ['translationLogger'],
    ns: [localNamespace, remoteNamespace],
    fallbackNS: remoteNamespace,
    supportedLngs: supportedLanguages,
    interpolation: {
      escapeValue: false,
      defaultVariables: {
        brandName: brandConfig.name,
        appName: brandConfig.appName,
        companyName: brandConfig.companyName,
      },
    },
    backend: {
      loadPath: ([language]: string[], [namespace]: string[]) => {
        if (namespace === remoteNamespace) {
          return `/api/rest/v1/Localization/${language}`;
        }
        return `${process.env.PUBLIC_PATH}locales/${language}/${namespace}.json?v=${process.env.APP_VERSION}`;
      },
      parse: (data: string, lng: string, ns: string) => {
        if (ns === remoteNamespace) {
          const response = JSON.parse(data) as {
            culture: string;
            resources: { key: string; value: string; }[];
          };
          const translations: Record<string, string> = {};
          response.resources.forEach(
            ({ key, value }) => {
              translations[key] = value;
            },
          );
          return translations;
        }
        return JSON.parse(data);
      },
    },
  });

i18n
  .on('languageChanged', (lng: string) => {
    localStorageManager.saveItem(LOCAL_STORAGE_LANGUAGE_STORAGE_KEY, lng);
    document.documentElement.setAttribute(
      'lang',
      i18n.languages.includes(lng) ? lng : fallbackLng,
    );
  });

export default i18n;
